import { useEffect, useState } from "react";
import type { Role } from "@hopitech/contracts";
import { ErreurApi, adminComptes, ajouterRattachement, impersonate, retirerRattachement, type CompteAnnuaire } from "../lib/api.ts";
import { EDITIONS, ROLES_UI } from "../app/config.ts";
import { useApp } from "../app/state.tsx";
import { Panneau } from "./ui.tsx";

/** D16 — Comptes & rattachements, sur la vraie table. Attribution des rôles + bascule de compte. */
export function Comptes(): React.ReactElement {
  const app = useApp();
  const [comptes, setComptes] = useState<CompteAnnuaire[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("exposant");
  const [edition, setEdition] = useState<number>(EDITIONS[0]?.id ?? 12);

  async function charger(): Promise<void> {
    try {
      const r = await adminComptes();
      setComptes(r.comptes);
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof ErreurApi && (e.statut === 404 || e.statut === 403)
        ? "L'administration des comptes exige le mode Better Auth (base + secret configurés)."
        : e instanceof ErreurApi ? e.message : "Chargement impossible.");
    }
  }
  useEffect(() => { void charger(); }, []);

  async function ajouter(): Promise<void> {
    if (!email.trim()) return;
    try { await ajouterRattachement(email.trim(), role, edition); setEmail(""); await charger(); }
    catch (e) { setErreur(e instanceof ErreurApi ? e.message : "Ajout impossible."); }
  }
  async function retirer(mail: string, r: Role, ed: number): Promise<void> {
    try { await retirerRattachement(mail, r, ed); await charger(); }
    catch (e) { setErreur(e instanceof ErreurApi ? e.message : "Retrait impossible."); }
  }
  async function basculer(userId: string): Promise<void> {
    try { await impersonate(userId); await app.rafraichir(); }
    catch (e) { setErreur(e instanceof ErreurApi ? e.message : "Bascule impossible."); }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 900 }}>
      {erreur && <div className="pill pill-danger" style={{ alignSelf: "flex-start", textTransform: "none", fontFamily: "var(--sans)", fontSize: 12.5 }}>{erreur}</div>}

      <Panneau titre="Attribuer un rôle" sous="Le rôle porte sur le rattachement (compte × édition). Aucun mot de passe n'est affiché.">
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div className="field" style={{ flex: "2 1 220px" }}>
            <label>Adresse e-mail du compte</label>
            <input className="input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="personne@etablissement.fr" />
          </div>
          <div className="field" style={{ flex: "1 1 140px" }}>
            <label>Rôle</label>
            <select className="select" value={role} onChange={(e) => setRole(e.target.value as Role)}>
              {ROLES_UI.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
          </div>
          <div className="field" style={{ flex: "1 1 120px" }}>
            <label>Édition</label>
            <select className="select" value={edition} onChange={(e) => setEdition(Number(e.target.value))}>
              {EDITIONS.map((ed) => <option key={ed.id} value={ed.id}>{ed.optionLabel}</option>)}
            </select>
          </div>
          <button className="btn btn-primary" onClick={() => void ajouter()}>Attribuer</button>
        </div>
      </Panneau>

      <Panneau titre="Comptes" sous={comptes ? `${comptes.length} compte(s).` : "Chargement…"}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {(comptes ?? []).map((c) => (
            <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", border: "1px solid var(--line)", borderRadius: 10, flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{c.nom}</div>
                <div style={{ fontSize: 12.5, color: "var(--muted)" }}>{c.email}</div>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", flex: "2 1 260px" }}>
                {c.rattachements.length === 0 && <span style={{ fontSize: 12.5, color: "var(--faint-2)" }}>aucun rôle</span>}
                {c.rattachements.map((r) => (
                  <button key={`${r.role}-${r.edition}`} onClick={() => void retirer(c.email, r.role, r.edition)}
                    title="Retirer ce rattachement"
                    className="pill pill-accent" style={{ textTransform: "none", fontFamily: "var(--sans)", fontSize: 11.5, border: 0, cursor: "pointer" }}>
                    {ROLES_UI.find((x) => x.id === r.role)?.label} · {r.edition} ✕
                  </button>
                ))}
              </div>
              {app.mode === "better-auth" && app.role === "administrateur" && c.email !== app.compte && (
                <button className="btn btn-ghost" style={{ padding: "6px 11px", fontSize: 12 }} onClick={() => void basculer(c.id)}>Basculer</button>
              )}
            </div>
          ))}
        </div>
      </Panneau>
    </div>
  );
}
